import { DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent, Layout, Textbox } from "@mcleod/components";
import { AutogenLayoutDetentionDefaults } from "./autogen/AutogenLayoutDetentionDefaults";
import { DuplicateId, DuplicateIdProps } from "./DuplicateId";
import { DateRange, ModelRow } from "@mcleod/core";
import { DetentionHistoryList } from "./DetentionHistoryList";

const viewDefaultsURL = "lme/dispatch/DetentionDefaults?mode=update&key=";

const DUPLICATE_VALIDATION_PROPS: DuplicateIdProps = {
    tableName: "detention_profile",
    screenName: "Detention Defaults",
    navigateToUrl: viewDefaultsURL
};

export class DetentionDefaults extends AutogenLayoutDetentionDefaults {

    sourceDetentionProfileAfterModeChange(event: DataSourceModeChangeEvent) {
        if (DataSourceMode.ADD === event.newMode) {
            DuplicateId.configureIdTextbox(this.textboxId, DUPLICATE_VALIDATION_PROPS);
        }
    }

    sourceDetentionProfileBeforeExecution(event: DataSourceExecutionEvent) {
        if (DataSourceMode.ADD === this.mainDataSource.mode
            && this.activeRow
            && this.activeRow.isNull("location_type")) {
            this.activeRow.set("location_type", "");
        }
    }

    static mapDefaultsToHistorySearch(rowDefaults: ModelRow, layout: DetentionHistoryList) {
        const searchRow = layout.mainDataSource.searchRow;

        const promises: Promise<void>[] = [];

        const defaultId = rowDefaults.getString("id", null);
        searchRow.set("detention_profile_id", defaultId);
        promises.push(this.applyLookupModelData(layout.textboxDefaults, searchRow));

        if (!rowDefaults.isNull("status")) {
            const statuses = rowDefaults.getString("status", null);
            searchRow.set("status", statuses);
        }
        if (!rowDefaults.isNull("detention_charge_status")) {
            const detChgStatuses = rowDefaults.getString("detention_charge_status", null);
            searchRow.set("charge_status", detChgStatuses);
        }
        if (!rowDefaults.isNull("detention_payment_status")) {
            const detPayStatuses = rowDefaults.getString("detention_payment_status", null);
            searchRow.set("pay_status", detPayStatuses);
        }
        if (!rowDefaults.isNull("payment_approval")) {
            const paymentApprovals = rowDefaults.getString("payment_approval", null);
            searchRow.set("pay_approval", paymentApprovals);
        }
        if (!rowDefaults.isNull("start_day")) {
            const startDay = rowDefaults.getString("start_day", "t");
            const daysOut = rowDefaults.getNumber("creation_days", -1);
            const creationDateRange = (daysOut >= 0)
                ? DateRange.createWithDaysOut(startDay, daysOut)
                : DateRange.parseDateRangeWithKeywords(`>${startDay}`);
            searchRow.set("creation_date", creationDateRange);
        }
        if (!rowDefaults.isNull("billing_approved") && rowDefaults.getString("billing_approved", null) !== "B") {
            layout.checkboxBillApproved.checked = rowDefaults.getString("billing_approved", null) === "Y";
        }
        if (!rowDefaults.isNull("auth_blank")) {
            layout.checkboxAuthNumBlank.checked = rowDefaults.getBoolean("auth_blank")
        }
        if (!rowDefaults.isNull("auth_status")) {
            const authStatuses = rowDefaults.getString("auth_status", null);
            searchRow.set("authorization_status", authStatuses);
            promises.push(this.applyLookupModelData(layout.textboxAuthorizationStatusSearch, searchRow));
        }
        if (!rowDefaults.isNull("operations_user_id")) {
            const opsUsers = rowDefaults.getString("operations_user_id", null);
            searchRow.set("movement.operations_user", opsUsers);
            promises.push(this.applyLookupModelData(layout.textboxMovementoperationsUser, searchRow));
        }
        if (!rowDefaults.isNull("customer_id")) {
            const customerIds = rowDefaults.getString("customer_id", null);
            searchRow.set("customer_id", customerIds);
            promises.push(this.applyLookupModelData(layout.textboxCustomerIdSearch, searchRow));
        }
        if (!rowDefaults.isNull("locations_list")) {
            const locationList = rowDefaults.getString("locations_list", null);
            searchRow.set("stop_location_id", locationList);
            promises.push(this.applyLookupModelData(layout.textboxStopLocationId, searchRow));
        }
        return Promise.all(promises).then(() => layout.mainDataSource.displayDataInBoundSearchComponents());
    }

    private static applyLookupModelData(textbox: Textbox, modelRow: ModelRow): Promise<void> {
        const lookupLayout = Layout.getLayout(textbox.lookupModelLayout);
        return lookupLayout.ensureLoaded().then(() => {
            const fieldValue = modelRow.get(textbox.field);
            const filter = {
                [textbox.lookupModelResultField]: (fieldValue.indexOf(",") >= 0) ? `in ${fieldValue}` : fieldValue
            };
            return lookupLayout.mainDataSource.search(filter, null, { layoutName: lookupLayout.layoutName }).then(response => {
                if (response?.modelRows?.length === 1) {
                    modelRow.setValueAndLookupModelData(textbox.field, modelRow.get(textbox.field), response.modelRows[0]);
                } else if (response?.modelRows?.length > 1) {
                    modelRow.setValueAndLookupModelData(textbox.field, modelRow.get(textbox.field), response.modelRows.map(row => ({
                        [textbox.lookupModelDisplayField]: row.get(textbox.lookupModelDisplayField),
                        [textbox.lookupModelResultField]: row.get(textbox.lookupModelResultField)
                    })));
                } else {
                    const lookupModelData = new ModelRow(modelRow._modelPath, false);
                    lookupModelData.set(textbox.lookupModelResultField, modelRow.get(textbox.field));
                    lookupModelData.set(textbox.lookupModelDisplayField, modelRow.get(textbox.field));
                    modelRow.setValueAndLookupModelData(textbox.field, modelRow.get(textbox.field), lookupModelData);
                }
            });
        })
    }
}
